import graphql from "babel-plugin-relay/macro";
import { FormProvider, FormState, useForm } from "react-hook-form";
import { useFragment, useMutation } from "react-relay";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";
import Spinner from "./Spinner";

import { useCallback, useEffect, useMemo, useState } from "react";
import InspectionFormDataContainer from "./InspectionForm.info";
import InspectionFormNavigation from "./InspectionForm.nav";
import InspectionFormReview from "./InspectionForm.review";

import { InspectionForm$key } from "./__generated__/InspectionForm.graphql";
import { InspectionStateEnum } from "./__generated__/InspectionFormOverview.graphql";
import { getWindowHeight, isStepValid } from "../utils";
import { Image, useImageUploader } from "../hooks/useImageUploader";
import InspectionFormConditionsContainer from "./InspectionForm.conditions";
import InspectionFormStep from "./InspectionForm.steps";
import InspectionSubmitModal from "./InspectionSubmitModal";
import FieldDetails from "./FieldDetails";
import LoaderModal from "./LoaderModal";

export enum Mode {
  FORM = "form",
  REVIEW = "review",
  INFO = "info",
  CONDITIONS = "conditions",
  DETAILS = "details",
}

export enum StepType {
  closed = "closed",
  open = "open",
  checklist = "checklist",
  multipleChoice = "multipleChoice",
  radio = "radio",
  photo = "photo",
}

export interface Step {
  key: string;
  type: StepType;
  title: string;
  choices?: string[];
  add_comment?: boolean;
  add_photo?: boolean;
  add_mandatory?: boolean;
  add_multiple?: boolean;
  countable?: boolean;
}

export interface IFormValues {
  [key: string]: any;
}

const InspectionFormContainer = (props: {
  inspection: InspectionForm$key;
  onFormStateChange: (formState: FormState<any>) => void;
}) => {
  const [mode, setMode] = useState<Mode>(Mode.FORM);
  const [showSubmitInspectionModal, setShowSubmitInspectionModal] =
    useState(false);

  const data = useFragment(
    graphql`
      fragment InspectionForm on InspectionNode {
        id
        inspectionform {
          steps
          addAdvice
          addPhotoEndresult
        }
        inspectionfield {
          plannedDate
          state
          year
          managementPackageSetting {
            pdfFileUrl
          }
          field {
            ...FieldMap
            ...FieldDetailsFragment
          }
        }
        images {
          edges {
            node {
              key
              metaData
              filename
              downloadUrl
              thumbnailUrl
              uploadedOn
            }
          }
        }
        approvedDate
        rejectedDate
        executionDate
        retryDate
        retry
        answers
        notes
        ...InspectionForm_data
      }
    `,
    props.inspection
  );

  const inspectionCompleted = Boolean(
    data.inspectionfield.state &&
      ["APPROVED", "REJECTED", "RETRY"].includes(data.inspectionfield.state)
  );

  const {
    images,
    addImages,
    deleteImage,
    uploadImages,
    resetImages,
    uploading,
    setImageDescription,
  } = useImageUploader(
    data?.images?.edges?.map((edge) => ({
      key: edge?.node?.key || "",
      downloadUrl: edge?.node?.downloadUrl || "",
      thumbnailUrl: edge?.node?.thumbnailUrl || "",
      metadata: JSON.parse(edge?.node?.metaData || "{}"),
      name: edge?.node?.filename || "",
      timestamp:
        new Date(edge?.node?.uploadedOn || new Date()).getTime() / 1000,
    })) || []
  );
  const steps: Step[] = useMemo(
    () => [
      ...JSON.parse(data?.inspectionform?.steps || "[]"),
      {
        key: "approval",
        type: "radio",
        title: "Eindoordeel",
        choices: data?.retry
          ? ["Akkoord", "Niet akkoord"]
          : ["Akkoord", "Herstel", "Niet akkoord"],
        add_comment: data?.inspectionform?.addAdvice,
        add_photo: data?.inspectionform?.addPhotoEndresult,
      },
    ],
    [
      data.inspectionform?.steps,
      data.retry,
      data?.inspectionform?.addAdvice,
      data?.inspectionform?.addPhotoEndresult,
    ]
  );
  const answers = useMemo(
    () => JSON.parse(data?.answers || "{}"),
    [data?.answers]
  );

  const [notes, setNotes] = useState<{ [key: string]: string }>(
    JSON.parse(data?.notes || "{}")
  );

  const [lastSavedTime, setLastSavedTime] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const form = useForm({ defaultValues: answers });
  const formValues = form.watch();
  const initialStep = useMemo(
    () =>
      data.retry
        ? steps[0]
        : [...steps].find((step, index) => !Boolean(answers[step.key])),
    [steps, answers, data.retry]
  );
  const [step, setStep] = useState(
    initialStep ? steps.indexOf(initialStep) : 0
  );
  const [edit, saving] = useMutation(
    graphql`
      mutation InspectionFormMutation(
        $id: ID!
        $steps: JSONString!
        $answers: JSONString!
        $images: [String!]
        $state: InspectionStateEnum
        $notes: JSONString!
        $descriptions: JSONString
        $executionDate: String
      ) {
        modifyInspection(
          input: {
            id: $id
            steps: $steps
            answers: $answers
            images: $images
            state: $state
            notes: $notes
            descriptions: $descriptions
            executionDate: $executionDate
          }
        ) {
          inspection {
            ...InspectionForm
            inspectionfield {
              ...InspectionFormOverview
            }
          }
        }
      }
    `
  );

  useEffect(() => {
    props.onFormStateChange(form.formState);
  }, [form.formState, props]);

  const handleNoteChange = (stepKey: string, note: string) => {
    setNotes({ ...notes, [stepKey]: note });
  };

  const handleClickReviewItem = useCallback((step: number) => {
    setMode(Mode.FORM);
    setStep(step);
  }, []);

  const getDescriptions = (images: Image[] | undefined) => {
    return JSON.stringify(
      images?.reduce((descriptions, image) => {
        return image.metadata &&
          Object.prototype.hasOwnProperty.call(image.metadata, "description")
          ? {
              ...descriptions,
              [image.key]: image.metadata?.description ?? "",
            }
          : descriptions;
      }, {} as Record<string, string>)
    );
  };

  const handleSave = async (
    executionDate?: string,
    state?: InspectionStateEnum
  ): Promise<boolean | string> => {
    setIsSaving(true);
    try {
      let newImages = await uploadImages();
      return await new Promise<boolean | string>((resolve) => {
        edit({
          variables: {
            id: data.id,
            steps: data?.inspectionform?.steps || "[]",
            answers: JSON.stringify(formValues || {}),
            images: newImages?.map(({ key }) => key),
            state: state,
            notes: JSON.stringify(notes || {}),
            descriptions: getDescriptions(newImages),
            executionDate: executionDate,
          },
          onCompleted: () => {
            form.reset(formValues);
            setLastSavedTime(new Date().toLocaleTimeString());
            resolve(true);
          },
          onError: (error) => {
            console.error("Could not connect to backend");
            resolve(false);
          },
        });
      });
    } catch (error) {
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const allStepsValid = useMemo(
    () =>
      steps.length ===
        steps.filter((step) => isStepValid(step, formValues[step.key], images))
          .length && Boolean(formValues["approval"]),
    [steps, formValues, images]
  );

  const getApprovalState = (
    approval?: string
  ): InspectionStateEnum | undefined =>
    approval?.toLowerCase() === "akkoord"
      ? "APPROVED"
      : approval?.toLowerCase() === "niet akkoord"
      ? "REJECTED"
      : approval?.toLowerCase() === "herstel"
      ? "RETRY"
      : undefined;

  const handleCompleteInspection = (inspectionDate: Date) => {
    const state = getApprovalState(formValues["approval"]);
    handleSave(inspectionDate.getTime().toString(), state);
    setShowSubmitInspectionModal(false);
  };

  const handleResetAnswers = () => {
    setNotes({});
    resetImages();
    form.reset(
      JSON.parse(data.inspectionform?.steps)?.reduce(
        (answers: any, step: any) => ({
          ...answers,
          [step.key]: null,
        }),
        {}
      )
    );
  };

  return (
    <Container>
      <InspectionFormNavigation
        onSave={handleSave}
        saving={uploading || saving}
        mode={mode}
        setMode={setMode}
        onComplete={
          allStepsValid ? () => setShowSubmitInspectionModal(true) : undefined
        }
        setLastSavedTime={setLastSavedTime}
        inspectionCompleted={inspectionCompleted}
        fieldYear={data?.inspectionfield?.year}
        inspectionCompletionDate={data?.executionDate}
      />
      <Body>
        <FormProvider {...form}>
          {mode === "form" ? (
            <InspectionFormStep
              key={`step_${step}`}
              step={step}
              steps={steps}
              images={images}
              notes={notes}
              lastSavedTime={lastSavedTime}
              isRetry={Boolean(data.retryDate)}
              onSave={handleSave}
              saving={uploading || saving}
              onStep={(step: number) => setStep(step)}
              onSwitchMode={(mode) => setMode(mode)}
              onReset={handleResetAnswers}
              onAddImages={(newImages, step, isOptional) => {
                addImages(
                  Object.values(newImages)?.map((image) => ({
                    path: URL.createObjectURL(image),
                    key: uuidv4(),
                    name: image.name,
                    timestamp: new Date().getTime() / 1000,
                    metadata: {
                      step,
                      optional: isOptional ? "true" : "false",
                    },
                  })) || []
                );
              }}
              onDeleteImage={deleteImage}
              onChangeDescription={(images, key, description) => {
                setImageDescription(images, key, description);
              }}
              onNoteSave={handleNoteChange}
            />
          ) : null}
          {mode === "info" ? (
            <InspectionFormDataContainer inspection={data} />
          ) : null}
          {mode === Mode.CONDITIONS && (
            <InspectionFormConditionsContainer
              pdfFileUrl={
                data?.inspectionfield?.managementPackageSetting?.pdfFileUrl
              }
            />
          )}
          {mode === "review" ? (
            <>
              <InspectionFormReview
                steps={steps}
                answers={formValues}
                images={images}
                onStep={handleClickReviewItem}
                inspectionCompleted={inspectionCompleted}
                notes={notes}
              />
              <InspectionSubmitModal
                show={showSubmitInspectionModal}
                plannedDate={new Date(data.inspectionfield.plannedDate)}
                onDismiss={() => setShowSubmitInspectionModal(false)}
                onSubmit={handleCompleteInspection}
                fieldYear={data?.inspectionfield?.year}
              />
            </>
          ) : null}
          {mode === "details" && data.inspectionfield.field ? (
            <FieldDetails field={data.inspectionfield.field} />
          ) : null}
        </FormProvider>
      </Body>
      <LoaderModal show={isSaving} />
    </Container>
  );
};

const InspectionFormFallback = () => {
  return (
    <Container>
      <InspectionFormNavigation mode={Mode.FORM} />
      <InspectionFormBody>
        <Spinner />
      </InspectionFormBody>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(${getWindowHeight()} - 73px);
  background: #fafafa;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  height: 100%;
`;

const InspectionFormBody = styled.form`
  /* Dropshadow */
  background: #fafafa;
  box-shadow: 0px 10px 15px rgba(168, 168, 168, 0.2);
  border-radius: 5px;
  align-self: center;
  width: 70vw;
  padding-left: 24px;
  padding-right: 24px;
  padding-top: 48px;
  padding-bottom: 48px;
  @media only screen and (max-width: 480px) {
    height: 100%;
    width: 100%;
  }
`;

export const InspectionInfoBody = styled(InspectionFormBody)`
  width: 40vw;
  background: white;
  margin-top: 38px;
  padding-top: 24px;
  padding-bottom: 24px;
  /* Dropshadow */
  border-radius: 5px;
  @media only screen and (max-width: 480px) {
    margin: unset;
    overflow: unset;
    height: 100%;
    width: 100%;
  }
`;

export { InspectionFormBody, InspectionFormFallback };

export default InspectionFormContainer;
